import { getFirestore, collection, query, where, getDocs } from 'firebase/firestore';
import firebaseApp from '../firebase/credenciales';

/**
 * MOTOR DE AUDITORÍA CALI - VERSIÓN 2026 (UIT S/ 5,500 | RMV S/ 1,130)
 * Especializado en Renta de 5ta Histórica, Regímenes MYPE y Validación de Afectaciones.
 */

const db = getFirestore(firebaseApp);

/**
 * Función Principal de Auditoría (Motor 100% Determinístico en JavaScript - Sin costo de Gemini)
 * @param {number} mesActualNum - (1 al 12)
 * @param {Object} dataPlanilla - Registro actual de pl_planilla_detalle
 * @param {Array} historialAnual - Array de registros previos del mismo empleado
 */
export const ejecutarAuditoriaCali2026 = async (mesActualNum, dataPlanilla, historialAnual = []) => {

    const VALORES_2026 = {
        UIT: 5500,
        RMV: 1130,
        ASIG_FAMILIAR: 113.00,
        DEDUCCION_7UIT: 38500
    };

    // 1. CAPA DE PRE-PROCESAMIENTO Y ACUMULADOS (JS)
    const acumuladoIngresosAfectos = historialAnual.reduce((acc, curr) => acc + (curr.total_ingresos_afectos || 0), 0);
    const acumuladoRetenciones5ta = historialAnual.reduce((acc, curr) => acc + (curr.descuentos?.renta_5ta || 0), 0);

    // Proyección de 5ta CORREGIDA:
    // - Ingresos previos del año (historial)
    // - Ingreso del mes actual  
    // - Proyección de meses faltantes (hasta Diciembre)
    // - 2 Gratificaciones (Julio y Diciembre)

    const baseMensualProyectable = Number(dataPlanilla.sueldo_base) + Number(dataPlanilla.asignacion_familiar);
    const mesesFaltantes = 12 - mesActualNum; // Meses después del actual hasta Diciembre

    const proyeccionAnual5ta = acumuladoIngresosAfectos +                      // Ej: Ingresos de Enero
        Number(dataPlanilla.total_ingresos_afectos) +  // Ej: Febrero (mes actual)
        (baseMensualProyectable * mesesFaltantes) +    // Ej: Marzo-Dic (10 meses)
        (baseMensualProyectable * 2);                  // 2 Gratificaciones

    // Clasificación de movilidad y condiciones de trabajo
    const ingresosNoAfectos = Number(dataPlanilla.total_ingresos_bruto) - Number(dataPlanilla.total_ingresos_afectos);

    const contextoAuditoria = {
        nombres: dataPlanilla.nombres,
        dni: dataPlanilla.empleado_id,
        regimen: dataPlanilla.regimen_aplicado || "GENERAL",
        mes_auditoria: mesActualNum,
        remuneracion: {
            sueldo_base: dataPlanilla.sueldo_base,
            total_bruto: dataPlanilla.total_ingresos_bruto,
            total_afecto: dataPlanilla.total_ingresos_afectos,
            no_afecto_movilidad: ingresosNoAfectos,
            asignacion_familiar: dataPlanilla.asignacion_familiar
        },
        renta_5ta: {
            retenido_mes: dataPlanilla.descuentos.renta_5ta,
            acumulado_retenciones: acumuladoRetenciones5ta, // Retenciones YA pagadas
            acumulado_ingresos: acumuladoIngresosAfectos,    // Ingresos del año previos
            proyeccion_anual: proyeccionAnual5ta,
            meses_faltantes: mesesFaltantes
        },
        provisiones: dataPlanilla.provisiones,
        aportaciones: dataPlanilla.aportaciones,
        descuentos_ley: {
            afp_onp: dataPlanilla.descuentos.sistema_pension?.total || dataPlanilla.descuentos.sistema_pension?.onp,
            tardanzas_faltas: (dataPlanilla.descuentos.descuento_tardanzas || 0) + (dataPlanilla.descuentos.descuento_faltas || 0)
        }
    };

    // 2. PROMPT DE AUDITORÍA MAESTRA (Cali)
    const prompt = `
Eres CALI, Auditor Senior de Planillas en Perú. Tu mandato es la intolerancia al error matemático y legal en el año 2026.

REGLAS DE AUDITORÍA ESTRICTA:

1. **RÉGIMEN LABORAL**:
   - GENERAL: Prov. Grati = 1/6. Prov. CTS = (Bruto + 1/6 Grati)/12. Asig. Fam = S/ ${VALORES_2026.ASIG_FAMILIAR} (SOLO si tiene hijos < 18 años - NO marcar CRITICO si es S/ 0, pero SÍ emitir ADVERTENCIA sugiriendo verificar carga familiar).
   - PEQUEÑA: Prov. Grati = 1/12. Prov. CTS = 50% del cálculo General.
   - MICRO: No Grati ni CTS. Si existen montos, marca ADVERTENCIA (Sobrecosto).

2. **RENTA DE 5TA (CÁLCULO HISTÓRICO)**:
   - Toma 'proyeccion_anual'. Resta S/ ${VALORES_2026.DEDUCCION_7UIT}.
   - Aplica escalas 2026: 8% (hasta 5 UIT=27,500), 14% (hasta 20 UIT=110,000), 17% (hasta 35 UIT=192,500), 20% (hasta 45 UIT=247,500), 30% (exceso).
   - Impuesto Anual Total = aplicar escalas progresivas.
   - Falta Retener = Impuesto Anual - 'acumulado_retenciones'.
   - Meses para distribuir = 'meses_faltantes' + 1 (incluye mes actual).
   - Retención Mensual Correcta = Falta Retener / Meses para distribuir.
   - Si 'retenido_mes' difiere por > S/ 1.00, marca CRITICO con cálculo detallado.

3. **MOVILIDAD Y AFECTACIÓN**:
   - Verifica que 'no_afecto_movilidad' no haya sido incluido en 'total_afecto'.
   - Si el trabajador tiene faltas/tardanzas, estas DEBEN haber reducido el 'total_afecto'. Si no, marca CRITICO.

4. **APORTACIONES**: 
   - EsSalud = 9% del 'total_afecto'. SCTR Salud/Pensión obligatorios si riesgo > 0.

DATOS A AUDITAR:
${JSON.stringify(contextoAuditoria)}

RESPUESTA JSON:
{
  "aprobado": boolean,
  "observaciones": [
    { "tipo": "CRITICO" | "ADVERTENCIA", "campo": "string", "mensaje": "string", "valor_esperado": "string" }
  ],
  "analisis_5ta": { "proyeccion": number, "impuesto_anual": number, "retencion_calculada": number }
}
`;

    // CÁLCULO EXACTO DE RENTA 5TA EN JAVASCRIPT (No confiar en IA para aritmética)
    const rentaNeta = Math.max(0, proyeccionAnual5ta - VALORES_2026.DEDUCCION_7UIT);
    let impuestoAnual = 0;

    if (rentaNeta > 0) {
        const escalas = [
            { hasta: 5 * VALORES_2026.UIT, tasa: 0.08 },
            { hasta: 20 * VALORES_2026.UIT, tasa: 0.14 },
            { hasta: 35 * VALORES_2026.UIT, tasa: 0.17 },
            { hasta: 45 * VALORES_2026.UIT, tasa: 0.20 },
            { hasta: Infinity, tasa: 0.30 }
        ];

        let baseRestante = rentaNeta;
        let tramoAnterior = 0;

        for (const escala of escalas) {
            const tramoHasta = escala.hasta;
            const tramoMonto = tramoHasta - tramoAnterior;
            const montoEnTramo = Math.min(baseRestante, tramoMonto);

            if (montoEnTramo > 0) {
                impuestoAnual += montoEnTramo * escala.tasa;
                baseRestante -= montoEnTramo;
                tramoAnterior = tramoHasta;
            }

            if (baseRestante <= 0 || tramoHasta === Infinity) break;
        }
    }

    const faltaRetener = impuestoAnual - acumuladoRetenciones5ta;
    const mesesParaDistribuir = mesesFaltantes + 1; // Incluye mes actual
    const retencionMensualCorrecta = faltaRetener / mesesParaDistribuir;

    try {
        // Motor determinístico 100% en JavaScript sin depender ni consumir Gemini API
        const auditResult = {
            aprobado: true,
            observaciones: [],
            analisis_5ta: {}
        };

        // 1. Validar Renta 5ta con EL CÁLCULO JS (ignorar lo que diga Gemini)
        const retencionReal = dataPlanilla.descuentos.renta_5ta;
        const diferenciaRenta = Math.abs(retencionMensualCorrecta - retencionReal);

        // Sobrescribir el análisis de la IA con nuestro cálculo exacto
        auditResult.analisis_5ta = {
            proyeccion: proyeccionAnual5ta,
            impuesto_anual: impuestoAnual,
            retencion_calculada: retencionMensualCorrecta
        };

        // Solo marcar error si hay diferencia significativa Y al menos uno no es cero
        if (diferenciaRenta > 1.00 && (retencionMensualCorrecta > 0 || retencionReal > 0)) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "renta_5ta",
                mensaje: `Retención de 5ta incorrecta. Esperado: S/ ${retencionMensualCorrecta.toFixed(2)}, Encontrado: S/ ${retencionReal.toFixed(2)} (Dif: S/ ${diferenciaRenta.toFixed(2)})`,
                valor_esperado: retencionMensualCorrecta.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 2. Validar Asignación Familiar (solo monto incorrecto, no ausencia)
        const asigFamiliar = Number(dataPlanilla.asignacion_familiar || 0);
        const sueldoBase = Number(dataPlanilla.sueldo_base || 0);

        if (asigFamiliar > 0 && asigFamiliar !== VALORES_2026.ASIG_FAMILIAR) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "asignacion_familiar",
                mensaje: `Asignación familiar con monto incorrecto. Debe ser exactamente S/ ${VALORES_2026.ASIG_FAMILIAR.toFixed(2)}, encontrado: S/ ${asigFamiliar.toFixed(2)}`,
                valor_esperado: VALORES_2026.ASIG_FAMILIAR.toFixed(2)
            });
            auditResult.aprobado = false;
        }
        // Nota: La advertencia de AF ausente la maneja Gemini con más contexto

        // 3. Validar EsSalud (debe ser exactamente 9% del total afecto)
        const essaludEnPlanilla = dataPlanilla.aportaciones?.essalud || 0;
        const totalAfecto = Number(dataPlanilla.total_ingresos_afectos || 0);
        const essaludEsperado = totalAfecto * 0.09;
        const diferenciaEssalud = Math.abs(essaludEnPlanilla - essaludEsperado);

        if (diferenciaEssalud > 0.50) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "essalud",
                mensaje: `EsSalud incorrecto. Debe ser 9% de S/ ${totalAfecto.toFixed(2)} = S/ ${essaludEsperado.toFixed(2)}, encontrado: S/ ${essaludEnPlanilla.toFixed(2)} (Dif: S/ ${diferenciaEssalud.toFixed(2)})`,
                valor_esperado: essaludEsperado.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 4. Validar AFP/ONP (verificar que exista descuento si es afiliado)
        const sistemaPension = dataPlanilla.descuentos?.sistema_pension;
        const regimenPensionario = dataPlanilla.regimen_pensionario;

        if (regimenPensionario === "AFP" && sistemaPension) {
            const fondoAfp = Number(sistemaPension.fondo || 0);
            const comisionAfp = Number(sistemaPension.comision || 0);
            const seguroAfp = Number(sistemaPension.seguro || 0);

            // Validar que el fondo sea aproximadamente 10% del afecto
            const fondoEsperado = totalAfecto * 0.10;
            const diferenciaFondo = Math.abs(fondoAfp - fondoEsperado);

            if (diferenciaFondo > totalAfecto * 0.01) { // Tolerancia 1% (más estricto)
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: "afp_fondo",
                    mensaje: `Fondo AFP sospechoso. Esperado ~10% de S/ ${totalAfecto.toFixed(2)} = S/ ${fondoEsperado.toFixed(2)}, encontrado: S/ ${fondoAfp.toFixed(2)} (Dif: S/ ${diferenciaFondo.toFixed(2)})`,
                    valor_esperado: fondoEsperado.toFixed(2)
                });
                auditResult.aprobado = false;
            }

            // Validar que haya comisión y seguro si hay fondo
            if (fondoAfp > 0 && (comisionAfp === 0 || seguroAfp === 0)) {
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: "afp_componentes",
                    mensaje: `AFP incompleta. Hay fondo (S/ ${fondoAfp.toFixed(2)}) pero falta comisión o seguro.`,
                    valor_esperado: "Comisión y Seguro obligatorios"
                });
                auditResult.aprobado = false;
            }
        } else if (regimenPensionario === "ONP" && sistemaPension) {
            const onp = Number(sistemaPension.onp || 0);
            const onpEsperada = totalAfecto * 0.13;
            const diferenciaOnp = Math.abs(onp - onpEsperada);

            if (diferenciaOnp > 0.50) {
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: "onp",
                    mensaje: `ONP incorrecta. Debe ser 13% de S/ ${totalAfecto.toFixed(2)} = S/ ${onpEsperada.toFixed(2)}, encontrado: S/ ${onp.toFixed(2)}`,
                    valor_esperado: onpEsperada.toFixed(2)
                });
                auditResult.aprobado = false;
            }
        }

        // 5. Validar Provisiones (varía según régimen)
        const provisiones = dataPlanilla.provisiones || {};
        const regimen = dataPlanilla.regimen_aplicado || "GENERAL";
        const brutoMensual = Number(dataPlanilla.total_ingresos_bruto || 0);

        // 5.1 Provisión de Vacaciones (todos los regímenes)
        const vacacionesEsperadas = brutoMensual / 12;
        const vacacionesEnPlanilla = Number(provisiones.vacaciones || 0);
        const difVacaciones = Math.abs(vacacionesEnPlanilla - vacacionesEsperadas);

        if (difVacaciones > 0.50) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "provision_vacaciones",
                mensaje: `Provisión de vacaciones incorrecta. Debe ser Bruto/12 = S/ ${brutoMensual.toFixed(2)}/12 = S/ ${vacacionesEsperadas.toFixed(2)}, encontrado: S/ ${vacacionesEnPlanilla.toFixed(2)}`,
                valor_esperado: vacacionesEsperadas.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 5.2 Provisión de Gratificaciones (depende del régimen)
        const gratiEnPlanilla = Number(provisiones.gratificaciones || 0);
        let gratiEsperada = 0;

        if (regimen === "GENERAL") {
            gratiEsperada = brutoMensual / 6;
        } else if (regimen === "MYPE_PEQUENA" || regimen === "PEQUEÑA") {
            gratiEsperada = brutoMensual / 12;
        } else if (regimen === "MYPE_MICRO" || regimen === "MICRO") {
            gratiEsperada = 0;
        }

        const difGrati = Math.abs(gratiEnPlanilla - gratiEsperada);

        if (difGrati > 0.50) {
            const formula = regimen === "GENERAL" ? "Bruto/6" :
                regimen.includes("PEQUENA") ? "Bruto/12" : "0";
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "provision_gratificaciones",
                mensaje: `Provisión de gratificaciones incorrecta para régimen ${regimen}. Debe ser ${formula} = S/ ${gratiEsperada.toFixed(2)}, encontrado: S/ ${gratiEnPlanilla.toFixed(2)}`,
                valor_esperado: gratiEsperada.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 5.3 Provisión de CTS (depende del régimen)
        const ctsEnPlanilla = Number(provisiones.cts || 0);
        let ctsEsperada = 0;

        if (regimen === "GENERAL") {
            ctsEsperada = (brutoMensual + gratiEsperada) / 12;
        } else if (regimen === "MYPE_PEQUENA" || regimen === "PEQUEÑA") {
            const ctsGeneral = (brutoMensual + gratiEsperada) / 12;
            ctsEsperada = ctsGeneral * 0.5; // 50% del cálculo general
        } else if (regimen === "MYPE_MICRO" || regimen === "MICRO") {
            ctsEsperada = 0;
        }

        const difCts = Math.abs(ctsEnPlanilla - ctsEsperada);

        if (difCts > 0.50) {
            const formula = regimen === "GENERAL" ? "(Bruto + Grati/6)/12" :
                regimen.includes("PEQUENA") ? "50% × (Bruto + Grati/12)/12" : "0";
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "provision_cts",
                mensaje: `Provisión de CTS incorrecta para régimen ${regimen}. Debe ser ${formula} = S/ ${ctsEsperada.toFixed(2)}, encontrado: S/ ${ctsEnPlanilla.toFixed(2)}`,
                valor_esperado: ctsEsperada.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 5.4 EsSalud Vacaciones (9% de provisión vacaciones)
        const essaludVacEsperado = vacacionesEsperadas * 0.09;
        const essaludVacEnPlanilla = Number(provisiones.essalud_vacaciones || 0);
        const difEssaludVac = Math.abs(essaludVacEnPlanilla - essaludVacEsperado);

        if (difEssaludVac > 0.50) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "provision_essalud_vacaciones",
                mensaje: `EsSalud sobre vacaciones incorrecto. Debe ser 9% de S/ ${vacacionesEsperadas.toFixed(2)} = S/ ${essaludVacEsperado.toFixed(2)}, encontrado: S/ ${essaludVacEnPlanilla.toFixed(2)}`,
                valor_esperado: essaludVacEsperado.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 5.5 Bono Extraordinario (9% de provisión gratificaciones)
        const bonoExtraEsperado = gratiEsperada * 0.09;
        const bonoExtraEnPlanilla = Number(provisiones.bono_extraordinario || 0);
        const difBonoExtra = Math.abs(bonoExtraEnPlanilla - bonoExtraEsperado);

        if (difBonoExtra > 0.50) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "provision_bono_extraordinario",
                mensaje: `Bono Extraordinario incorrecto. Debe ser 9% de S/ ${gratiEsperada.toFixed(2)} = S/ ${bonoExtraEsperado.toFixed(2)}, encontrado: S/ ${bonoExtraEnPlanilla.toFixed(2)}`,
                valor_esperado: bonoExtraEsperado.toFixed(2)
            });
            auditResult.aprobado = false;
        }

        // 6. Validar GRATIFICACIÓN EFECTIVA (solo en Julio y Diciembre)
        if (mesActualNum === 7 || mesActualNum === 12) {
            const gratificacionPagada = Number(dataPlanilla.gratificacion || 0);

            if (gratificacionPagada > 0) {
                // Calcular gratificación esperada basada en el semestre
                const mesesSemestre = mesActualNum === 7 ? 6 : 6; // Ene-Jun o Jul-Dic
                const inicioSemestre = mesActualNum === 7 ? 1 : 7;

                // Obtener mejor remuneración del semestre del historial
                let mejorRemuneracion = brutoMensual;
                historialAnual.forEach(mes => {
                    const mesMes = parseInt(mes.periodo.split('-')[1]);
                    if (mesMes >= inicioSemestre && mesMes < inicioSemestre + 6) {
                        const remMes = Number(mes.total_ingresos_bruto || 0);
                        if (remMes > mejorRemuneracion) mejorRemuneracion = remMes;
                    }
                });

                const gratiEsperada = mejorRemuneracion + asigFamiliar;
                const difGratiPagada = Math.abs(gratificacionPagada - gratiEsperada);

                if (difGratiPagada > 1.00) {
                    auditResult.observaciones.push({
                        tipo: "CRITICO",
                        campo: "gratificacion_efectiva",
                        mensaje: `Gratificación ${mesActualNum === 7 ? 'Julio' : 'Diciembre'} incorrecta. Esperado: S/ ${gratiEsperada.toFixed(2)} (mejor remuneración S/ ${mejorRemuneracion.toFixed(2)} + AF S/ ${asigFamiliar.toFixed(2)}), encontrado: S/ ${gratificacionPagada.toFixed(2)}`,
                        valor_esperado: gratiEsperada.toFixed(2)
                    });
                    auditResult.aprobado = false;
                }
            } else if (regimen === "GENERAL") {
                // Si es GENERAL y no hay pago, advertir
                auditResult.observaciones.push({
                    tipo: "ADVERTENCIA",
                    campo: "gratificacion_efectiva",
                    mensaje: `Es mes de gratificación (${mesActualNum === 7 ? 'Julio' : 'Diciembre'}) pero no hay pago registrado. Verificar.`,
                    valor_esperado: "Gratificación esperada"
                });
            }
        }

        // 7. Validar CTS EFECTIVA (solo en Mayo y Noviembre)
        if (mesActualNum === 5 || mesActualNum === 11) {
            const ctsPagada = Number(dataPlanilla.cts || 0);

            if (ctsPagada > 0) {
                // Calcular CTS esperada (promedio últimos 6 meses + 1/6 grati + AF) × días/360
                let sumaIngresos = 0;
                let mesesContados = 0;

                historialAnual.forEach(mes => {
                    const mesMes = parseInt(mes.periodo.split('-')[1]);
                    const inicioPeriodo = mesActualNum === 5 ? 11 : 5; // Nov-Abr o May-Oct

                    // Contar 6 meses previos
                    if ((mesActualNum === 5 && (mesMes >= 11 || mesMes < 5)) ||
                        (mesActualNum === 11 && mesMes >= 5 && mesMes < 11)) {
                        sumaIngresos += Number(mes.total_ingresos_afectos || 0);
                        mesesContados++;
                    }
                });

                // Incluir mes actual
                sumaIngresos += totalAfecto;
                mesesContados++;

                const promedioMensual = mesesContados > 0 ? sumaIngresos / mesesContados : brutoMensual;
                const sextoGrati = promedioMensual / 6;
                const ctsEsperada = ((promedioMensual + sextoGrati + asigFamiliar) / 12) * 6; // 6 meses = 180 días

                const difCtsPagada = Math.abs(ctsPagada - ctsEsperada);

                if (difCtsPagada > 1.00) {
                    auditResult.observaciones.push({
                        tipo: "CRITICO",
                        campo: "cts_efectiva",
                        mensaje: `CTS ${mesActualNum === 5 ? 'Mayo' : 'Noviembre'} incorrecta. Esperado: S/ ${ctsEsperada.toFixed(2)} (promedio S/ ${promedioMensual.toFixed(2)} + 1/6 Grati + AF), encontrado: S/ ${ctsPagada.toFixed(2)}`,
                        valor_esperado: ctsEsperada.toFixed(2)
                    });
                    auditResult.aprobado = false;
                }
            } else if (regimen === "GENERAL") {
                auditResult.observaciones.push({
                    tipo: "ADVERTENCIA",
                    campo: "cts_efectiva",
                    mensaje: `Es mes de CTS (${mesActualNum === 5 ? 'Mayo' : 'Noviembre'}) pero no hay pago registrado. Verificar.`,
                    valor_esperado: "CTS esperada"
                });
            }
        }

        // 8. Validar VACACIONES PAGADAS
        const vacacionesPagadas = Number(dataPlanilla.vacaciones || 0);
        if (vacacionesPagadas > 0) {
            const sueldoDiario = brutoMensual / 30;
            const diasMaximos = 30;
            const pagoMaximo = sueldoDiario * diasMaximos;

            if (vacacionesPagadas > pagoMaximo + 1) {
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: "vacaciones_pagadas",
                    mensaje: `Pago de vacaciones excesivo. Máximo 30 días × S/ ${sueldoDiario.toFixed(2)} = S/ ${pagoMaximo.toFixed(2)}, encontrado: S/ ${vacacionesPagadas.toFixed(2)}`,
                    valor_esperado: `≤ ${pagoMaximo.toFixed(2)}`
                });
                auditResult.aprobado = false;
            }
        }

        // 9. Validar ADELANTOS
        const adelantosArray = dataPlanilla.adelantos || [];
        const adelantosDescuento = Number(dataPlanilla.descuentos?.adelantos || 0);

        if (adelantosArray.length > 0) {
            const sumaAdelantos = adelantosArray.reduce((sum, adel) => sum + Number(adel.monto || 0), 0);
            const difAdelantos = Math.abs(sumaAdelantos - adelantosDescuento);

            if (difAdelantos > 0.01) {
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: "adelantos",
                    mensaje: `Suma de adelantos no coincide. Array suma S/ ${sumaAdelantos.toFixed(2)}, descuento registrado: S/ ${adelantosDescuento.toFixed(2)}`,
                    valor_esperado: sumaAdelantos.toFixed(2)
                });
                auditResult.aprobado = false;
            }
        }

        // 10. Validar BONOS (negativos y clasificación)
        const bonos = dataPlanilla.bonos || {};
        Object.keys(bonos).forEach(tipoBono => {
            const monto = Number(bonos[tipoBono] || 0);
            if (monto < 0) {
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: `bono_${tipoBono}`,
                    mensaje: `Bono ${tipoBono} tiene valor negativo: S/ ${monto.toFixed(2)}`,
                    valor_esperado: "≥ 0"
                });
                auditResult.aprobado = false;
            }
        });

        // 11. Validar HORAS EXTRAS (si existen registros)
        const asistencia = dataPlanilla.asistencia || dataPlanilla.horas_extras;
        if (asistencia) {
            const he25 = Number(asistencia.horas25 || 0);
            const he35 = Number(asistencia.horas35 || 0);
            const he100 = Number(asistencia.horas100 || 0);

            const ingresos = dataPlanilla.ingresos || {};
            const montoHE25 = Number(ingresos.he_25 || 0);
            const montoHE35 = Number(ingresos.he_35 || 0);
            const montoHE100 = Number(ingresos.he_100 || 0);

            if ((he25 > 0 || he35 > 0 || he100 > 0) && (montoHE25 + montoHE35 + montoHE100 === 0)) {
                auditResult.observaciones.push({
                    tipo: "CRITICO",
                    campo: "horas_extras",
                    mensaje: `Hay horas extras registradas (25%: ${he25}h, 35%: ${he35}h, 100%: ${he100}h) pero sin pago asociado`,
                    valor_esperado: "Pago de HE esperado"
                });
                auditResult.aprobado = false;
            }
        }

        // 12. Capa final de validación matemática (Neto)
        const bruto = Number(dataPlanilla.total_ingresos_bruto);
        const dcto = Number(dataPlanilla.total_descuentos);
        const neto = Number(dataPlanilla.neto_pagar);

        if (Math.abs(bruto - dcto - neto) > 0.01) {
            auditResult.observaciones.push({
                tipo: "CRITICO",
                campo: "neto_pagar",
                mensaje: `Error aritmético detectado en JS: El neto no cuadra con ingresos y descuentos.`,
                valor_esperado: (bruto - dcto).toFixed(2)
            });
            auditResult.aprobado = false;
        }

        return auditResult;
    } catch (error) {
        console.error("Fallo en Auditoría Cali:", error);
        return { aprobado: false, observaciones: [{ tipo: "CRITICO", mensaje: "Error de sistema: " + error.message }] };
    }
};

/**
 * WRAPPER DE COMPATIBILIDAD - auditarLote()
 * Mantiene la interfaz esperada por CaliAuditModal mientras usa la nueva lógica
 * @param {Object} contexto - Contexto con periodo, ruc, configuraciones
 * @param {Array} loteDetalles - Array de empleados a auditar
 */
export const auditarLote = async (contexto, loteDetalles) => {
    const observacionesConsolidadas = [];
    let tieneErroresCriticos = false;

    // Extraer mes del periodo (formato YYYY-MM)
    const [anio, mes] = (contexto.periodo || "2026-01").split('-');
    const mesActualNum = parseInt(mes);
    const ruc = contexto.ruc;

    // Procesar cada empleado del lote
    for (const empleado of loteDetalles) {
        try {
            // 1. Obtener historial del año para este empleado
            const historialAnual = await obtenerHistorialAnual(ruc, empleado.empleado_id, anio, mes);

            // 2. Ejecutar auditoría individual
            const resultado = await ejecutarAuditoriaCali2026(mesActualNum, empleado, historialAnual);

            // 3. Transformar observaciones al formato antiguo (compatibilidad)
            if (resultado.observaciones && resultado.observaciones.length > 0) {
                resultado.observaciones.forEach(obs => {
                    observacionesConsolidadas.push({
                        dni: empleado.empleado_id || empleado.dni,
                        nombres: empleado.nombres,
                        tipo: obs.tipo,
                        mensaje: obs.mensaje
                    });

                    if (obs.tipo === "CRITICO") {
                        tieneErroresCriticos = true;
                    }
                });
            }

        } catch (error) {
            observacionesConsolidadas.push({
                dni: empleado.empleado_id,
                nombres: empleado.nombres,
                tipo: "CRITICO",
                mensaje: `Error en procesamiento: ${error.message}`
            });
            tieneErroresCriticos = true;
        }
    }

    const resultado = {
        aprobado: !tieneErroresCriticos,
        observaciones: observacionesConsolidadas
    };

    return resultado;
};

/**
 * Función auxiliar para obtener historial anual de un empleado
 */
async function obtenerHistorialAnual(ruc, empleadoId, anio, mesActual) {
    try {
        const q = query(
            collection(db, "pl_planilla_detalle"),
            where("ruc_empresa", "==", ruc),
            where("empleado_id", "==", empleadoId),
            where("periodo", ">=", `${anio}-01`),
            where("periodo", "<", `${anio}-${mesActual}`)
        );

        const snapshot = await getDocs(q);
        const historial = [];

        snapshot.forEach(doc => {
            historial.push(doc.data());
        });

        return historial;
    } catch (error) {
        console.warn(`No se pudo obtener historial para ${empleadoId}:`, error);
        return []; // Retornar array vacío en caso de error
    }
}